import { useEffect, useMemo, useRef, useState } from 'react';
import DashboardLayout from '../../layouts/DashoardLayout';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItemButton,
  Menu,
  MenuItem,
  Pagination,
  Paper,
  SegmentedFilter,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '../../ui/core';
import {
  scrollY,
  softFill,
  surfaceCardSx as cardSx,
  floatSurfaceSx,
  cardHeaderSx,
  cardBodySx,
  flushPageSx,
  railItemSx,
} from '../../ui/styles';
import { DeleteIcon, MoreVertIcon, PlusIcon, SearchIcon, WarningIcon } from '../../ui/icons';
import { useToast } from '../../hooks';
import api from '../../lib/api';
import PromotionForm from '../../components/widgets/Promotions/PromotionForm';
import PromotionUsage from '../../components/widgets/Promotions/PromotionUsage';

const isDeactivated = (promo) => !!promo?.endsOn && new Date(promo.endsOn) <= new Date();

const optionLabel = (promo) => {
  const opt = promo.options && promo.options[0];
  if (!opt) return null;
  if (opt.type === 'percent') return `${opt.amount}% off`;
  if (opt.type === 'usd') return `$${opt.amount} off`;
  return null;
};

// "Applied" = accounts the code is attached to; "redeemed" = uses actually
// consumed on rides. Both come from the listing endpoint (appliedCount /
// redeemedCount); render nothing until a backend that supplies them is live,
// so the row degrades gracefully rather than showing a blank "· redeemed".
const hasUsageStats = (promo) =>
  typeof promo.appliedCount === 'number' && typeof promo.redeemedCount === 'number';

const PromotionUsageStats = ({ promo }) =>
  hasUsageStats(promo) ? (
    <Typography
      component="span"
      sx={{ fontSize: '11.5px', fontWeight: 500, color: (theme) => theme.palette.text.secondary }}
    >
      {promo.appliedCount.toLocaleString()} applied
      <Box component="span" sx={{ mx: '5px', color: 'text.inactive' }}>
        ·
      </Box>
      {promo.redeemedCount.toLocaleString()} redeemed
    </Typography>
  ) : null;

const PromotionListItem = ({ promo, selected, onClick }) => (
  <ListItemButton
    selected={selected}
    onClick={onClick}
    disableRipple
    sx={{ ...railItemSx, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '3px', py: '10px' }}
  >
    <Box display="flex" gap="8px" alignItems="center" width="100%">
      <Typography fontWeight={600} fontSize="14px" noWrap sx={{ flex: '0 1 auto', minWidth: 0 }}>
        {promo.code}
      </Typography>
      {optionLabel(promo) && (
        <Typography
          component="span"
          sx={{
            fontSize: '11px',
            fontWeight: 600,
            px: '6px',
            py: '1px',
            borderRadius: '5px',
            color: (theme) => theme.palette.text.secondary,
            backgroundColor: softFill,
            flexShrink: 0,
          }}
        >
          {optionLabel(promo)}
        </Typography>
      )}
      {isDeactivated(promo) && (
        <Box
          component="span"
          sx={{ ml: 'auto', fontSize: '11px', fontWeight: 500, color: (theme) => theme.palette.text.inactive, flexShrink: 0 }}
        >
          Inactive
        </Box>
      )}
    </Box>
    {(promo.subtitle || promo.description) && (
      <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: '100%', fontSize: '12.5px' }}>
        {promo.subtitle || promo.description}
      </Typography>
    )}
    <PromotionUsageStats promo={promo} />
  </ListItemButton>
);

// Secondary/destructive actions tucked behind a single affordance instead of
// competing buttons in the header (Apple-style restraint).
const PromotionActionsMenu = ({ promo, busy, onDeactivate, onDeleteRequest }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const close = () => setAnchorEl(null);
  return (
    <>
      <IconButton
        size="small"
        onClick={(e) => setAnchorEl(e.currentTarget)}
        disabled={busy}
        sx={{ borderRadius: '8px', '&:hover': { backgroundColor: softFill } }}
      >
        <MoreVertIcon sx={{ fontSize: 20 }} />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { minWidth: '180px', mt: '4px', ...floatSurfaceSx } }}
      >
        <MenuItem
          disabled={isDeactivated(promo)}
          onClick={() => {
            close();
            onDeactivate();
          }}
          sx={{ fontSize: '14px' }}
        >
          <WarningIcon sx={{ fontSize: 17, mr: '10px', color: 'text.secondary' }} />
          {isDeactivated(promo) ? 'Deactivated' : 'Deactivate'}
        </MenuItem>
        <MenuItem
          onClick={() => {
            close();
            onDeleteRequest();
          }}
          sx={{ fontSize: '14px', color: 'error.main' }}
        >
          <DeleteIcon sx={{ fontSize: 17, mr: '10px', color: 'error.main' }} />
          Delete
        </MenuItem>
      </Menu>
    </>
  );
};

const PAGE_SIZE = 20;

const PromotionsManager = () => {
  const t = useToast();
  const [promotions, setPromotions] = useState(null); // current page of results
  const [count, setCount] = useState(0); // total matching search/status (for pagination)
  const [counts, setCounts] = useState({ all: 0, active: 0, inactive: 0 }); // filter-chip totals
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState(null); // null = creating new
  const [saving, setSaving] = useState(false);
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // all | active | inactive
  const [deactivating, setDeactivating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [editorLoading, setEditorLoading] = useState(false);
  const [resetNonce, setResetNonce] = useState(0);
  const [reloadNonce, setReloadNonce] = useState(0);
  const [editorTab, setEditorTab] = useState('details'); // details | usage

  const pages = Math.ceil(count / PAGE_SIZE);

  // The All/Active/Inactive tabs partition the same dataset, so switching between
  // them shouldn't wait on the network. We cache each result by status+search+page
  // and warm the sibling tabs in the background after every load, so a tab switch
  // reads from cache and feels instant. Any mutation clears the cache via reload().
  const cacheRef = useRef(new Map());
  const keyFor = (status, search, pg) => `${status}|${search}|${pg}`;
  const reload = () => {
    cacheRef.current.clear();
    setReloadNonce((n) => n + 1);
  };

  // Warm a view into the cache without touching render state. Silent on error —
  // the real load surfaces problems; a failed prefetch just isn't cached.
  const prefetchView = (status, search, pg) => {
    const key = keyFor(status, search, pg);
    if (cacheRef.current.has(key)) return;
    api.promotions
      .getPromotions({ offset: (pg - 1) * PAGE_SIZE, limit: PAGE_SIZE, search, status })
      .then((res) => cacheRef.current.set(key, res))
      .catch(() => {});
  };

  // Search now hits the server, so debounce keystrokes into a single query.
  useEffect(() => {
    const id = setTimeout(() => setDebouncedQuery(query.trim()), 300);
    return () => clearTimeout(id);
  }, [query]);

  // A new search or status filter always returns to the first page.
  useEffect(() => {
    setPage(1);
  }, [debouncedQuery, statusFilter]);

  // Single loader: serves the current view from cache when possible (instant),
  // otherwise fetches. Either way it then warms the other two tabs (page 1, same
  // search) so switching to them is immediate. Runs whenever the page, search,
  // status, or an explicit reload (after a save/delete) changes.
  useEffect(() => {
    let cancelled = false;
    const apply = (res) => {
      setPromotions(res.data);
      setCount(res.count);
      setCounts(res.counts);
    };

    const cached = cacheRef.current.get(keyFor(statusFilter, debouncedQuery, page));
    if (cached) {
      apply(cached);
    } else {
      api.promotions
        .getPromotions({ offset: (page - 1) * PAGE_SIZE, limit: PAGE_SIZE, search: debouncedQuery, status: statusFilter })
        .then((res) => {
          if (cancelled) return;
          cacheRef.current.set(keyFor(statusFilter, debouncedQuery, page), res);
          apply(res);
        })
        .catch(() => {
          if (cancelled) return;
          t.error('Could not load promotions');
          setPromotions([]);
          setCount(0);
        });
    }

    // Warm the sibling tabs (first page, same search) for an instant switch.
    ['all', 'active', 'inactive']
      .filter((s) => s !== statusFilter)
      .forEach((s) => prefetchView(s, debouncedQuery, 1));

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, debouncedQuery, statusFilter, reloadNonce]);

  // The form stays mounted across switches (labels/structure stay put). Selecting
  // flips `editorLoading` so only the field values swap to a brief skeleton; this
  // clears it once the new promotion's values have synced in. resetNonce also
  // bumps this when the caller wants a reseed without `initial` itself changing
  // (e.g. clicking +New again while already on a blank/dirty new form).
  useEffect(() => {
    const timer = setTimeout(() => setEditorLoading(false), 160);
    return () => clearTimeout(timer);
  }, [selectedId, resetNonce]);

  const selectPromo = (id) => {
    if (id !== null && id === selectedId) return;
    if (id === null) setResetNonce((n) => n + 1);
    setSelectedId(id);
    setEditorLoading(true);
    // Usage is per-promo; always land on Details when the selection changes so
    // switching promos (or starting a new one) doesn't strand you on a stale tab.
    setEditorTab('details');
  };

  const selected = useMemo(
    () => (selectedId && promotions ? promotions.find((p) => p.id === selectedId) : null),
    [selectedId, promotions],
  );

  const onSubmit = async (input) => {
    setSaving(true);
    try {
      const saved = await api.promotions.createOrUpdatePromotion({ input });
      if (!saved) throw new Error('Promotion was not returned by the server');
      t.success(input.id ? `Updated ${saved.code}` : `Created ${saved.code}`);
      setSelectedId(saved.id);
      // A brand-new promo is newest, so it lands at the top of page 1; jump
      // there so it's visible and selected. Updates keep their place.
      // Always reload() so the cache is cleared and page 1 refetches fresh —
      // otherwise the prefetched page-1 entry would render without the new promo.
      if (!input.id && page !== 1) setPage(1);
      reload();
    } catch (e) {
      t.error(e?.message || 'Could not save promotion');
    } finally {
      setSaving(false);
    }
  };

  const onDeactivate = async () => {
    if (!selected) return;
    setDeactivating(true);
    try {
      await api.promotions.deactivatePromotion({ id: selected.id });
      t.success(`${selected.code} deactivated`);
      // A mutation can shrink the list below the current page's range, which
      // would strand the user on a now-empty page — return to page 1.
      setPage(1);
      reload();
    } catch (e) {
      t.error(e?.message || 'Could not deactivate promotion');
    } finally {
      setDeactivating(false);
    }
  };

  const onDelete = async () => {
    if (!selected) return;
    setDeleting(true);
    try {
      await api.promotions.deletePromotion({ id: selected.id });
      t.success(`${selected.code} deleted`);
      setShowDeleteConfirm(false);
      setSelectedId(null);
      // Delete shrinks the list, so a high page can end up empty — reset to 1.
      setPage(1);
      reload();
    } catch (e) {
      t.error(e?.message || 'Could not delete promotion');
    } finally {
      setDeleting(false);
    }
  };

  return (
    // Two floating cards on the gray page background. On desktop the row fills
    // the viewport and each card scrolls internally; on mobile they stack and
    // the page scrolls. flushPageSx is the shared scaffold every `flush`
    // section uses, so a spacing/sizing tweak applies everywhere at once.
    <Box sx={flushPageSx}>
      {/* Sub-nav card — the promo list */}
      <Paper
        elevation={0}
        sx={{ ...cardSx, width: { xs: '100%', md: 308 }, flexShrink: 0, maxHeight: { xs: 380, md: 'none' } }}
      >
        <Box sx={{ p: '16px', pb: '12px', flexShrink: 0 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb="12px">
            <Typography sx={{ fontSize: '15px', fontWeight: 600 }}>All promotions</Typography>
            <IconButton
              size="small"
              onClick={() => selectPromo(null)}
              sx={{ borderRadius: '8px', backgroundColor: softFill, width: 28, height: 28 }}
            >
              <PlusIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>
          <TextField
            fullWidth
            size="small"
            placeholder="Search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            InputProps={{ startAdornment: <SearchIcon sx={{ fontSize: 17, mr: '6px', color: 'text.secondary' }} /> }}
            sx={{
              mb: '10px',
              '& .MuiOutlinedInput-root': {
                borderRadius: '999px',
                fontSize: '14px',
                backgroundColor: softFill,
                fieldset: { border: 'none' },
              },
              '& input::placeholder': { color: 'text.inactive', opacity: 1, fontWeight: 400 },
            }}
          />
          <SegmentedFilter
            value={statusFilter}
            onChange={setStatusFilter}
            segments={[
              { key: 'all', label: 'All', count: counts.all },
              { key: 'active', label: 'Active', count: counts.active },
              { key: 'inactive', label: 'Inactive', count: counts.inactive },
            ]}
          />
        </Box>
        <Box sx={{ flex: 1, minHeight: 0, px: '8px', pb: '8px', ...scrollY }}>
          {!promotions && (
            <Box display="flex" justifyContent="center" p="24px">
              <CircularProgress size={22} />
            </Box>
          )}
          {promotions && promotions.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ px: '10px', py: '12px', fontSize: '13px' }}>
              No promotions match.
            </Typography>
          )}
          <List disablePadding>
            {(promotions || []).map((p) => (
              <PromotionListItem
                key={p.id}
                promo={p}
                selected={p.id === selectedId}
                onClick={() => selectPromo(p.id)}
              />
            ))}
          </List>
        </Box>
        {pages > 1 && (
          <Box
            sx={{
              flexShrink: 0,
              display: 'flex',
              justifyContent: 'center',
              p: '10px',
              borderTop: (theme) => `1px solid ${theme.palette.divider}`,
            }}
          >
            <Pagination count={pages} page={page} size="small" onChange={(_, value) => setPage(value)} />
          </Box>
        )}
      </Paper>

      {/* Content card — the editor */}
      <Paper elevation={0} sx={{ ...cardSx, flex: 1, minWidth: 0 }}>
        {/* When a promo is selected the tab bar owns the bottom hairline, so the
            header drops its own; a new promo has no tabs and keeps the standard
            header rule so it still separates from the form below. */}
        <Box sx={{ ...cardHeaderSx, ...(selected ? { pb: 0, borderBottom: 'none' } : {}) }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Stack spacing="2px">
              <Typography sx={{ fontSize: '19px', fontWeight: 600, letterSpacing: '-0.01em' }}>
                {selected ? selected.code : 'New promotion'}
              </Typography>
              <Typography color="text.secondary" sx={{ fontSize: '13px' }}>
                {selected ? 'Edit the details of this promo code' : 'Create a promo code, independent of any user'}
              </Typography>
            </Stack>
            {selected && (
              <PromotionActionsMenu
                promo={selected}
                busy={deactivating || deleting}
                onDeactivate={onDeactivate}
                onDeleteRequest={() => setShowDeleteConfirm(true)}
              />
            )}
          </Box>
          {/* Usage is only meaningful for a saved promo, so the tab bar is hidden
              while creating a new one — the Details form stands alone. */}
          {selected && (
            <Tabs
              value={editorTab}
              onChange={(_, v) => setEditorTab(v)}
              aria-label="promotion editor tabs"
              sx={{
                mt: '14px',
                minHeight: 0,
                borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                '& .MuiTab-root': {
                  minHeight: 0,
                  textTransform: 'none',
                  fontSize: '13.5px',
                  fontWeight: 600,
                  px: 0,
                  mr: '24px',
                  py: '10px',
                },
              }}
            >
              <Tab id="details" value="details" label="Details" aria-controls="promotion-tab-details" />
              <Tab id="usage" value="usage" label="Usage" aria-controls="promotion-tab-usage" />
            </Tabs>
          )}
        </Box>
        <Box sx={{ ...cardBodySx, ...scrollY }}>
          {/* Details stays mounted (just hidden) so in-progress edits survive a
              peek at Usage; the form owns its own draft state. Usage mounts only
              when active so it fetches fresh each time it's opened. */}
          <Box
            role="tabpanel"
            id="promotion-tab-details"
            sx={{ maxWidth: 760, width: '100%', display: selected && editorTab === 'usage' ? 'none' : 'block' }}
          >
            <PromotionForm
              initial={selected}
              resetSignal={resetNonce}
              loading={editorLoading}
              onSubmit={onSubmit}
              submitting={saving}
              submitLabel={selected ? 'Save changes' : 'Create promotion'}
            />
          </Box>
          {selected && editorTab === 'usage' && (
            <Box role="tabpanel" id="promotion-tab-usage" sx={{ width: '100%' }}>
              <PromotionUsage promo={selected} />
            </Box>
          )}
        </Box>
      </Paper>

      <Dialog open={showDeleteConfirm} onClose={() => setShowDeleteConfirm(false)} PaperProps={{ sx: { borderRadius: '16px' } }}>
        <DialogTitle>Delete promotion?</DialogTitle>
        <DialogContent sx={{ maxWidth: '400px' }}>
          <Typography>
            Are you sure you want to permanently delete{' '}
            <Typography component="span" sx={{ fontWeight: 700 }}>
              {selected?.code}
            </Typography>
            ? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: '24px', pb: '20px' }}>
          <Button onClick={() => setShowDeleteConfirm(false)} sx={{ borderRadius: '8px' }}>
            Cancel
          </Button>
          <Button variant="contained" color="error" loading={deleting} onClick={onDelete} sx={{ borderRadius: '8px' }}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const Promotions = () => (
  <DashboardLayout flush title="Promotions">
    <PromotionsManager />
  </DashboardLayout>
);

export default Promotions;
